// @ts-nocheck
import { refundDisplayId } from "../utils/refund";
import { cancelReasonLabel, payMethodLabel } from "@kawkaw/shared";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Skeleton } from "../components/admin-ui";
import { StatusBadge } from "../components/StatusBadge";
import {
  ArrowLeftOutlined,
  UserOutlined,
  TransactionOutlined,
  FileTextOutlined,
  DollarOutlined,
  ClockCircleFilled,
  InfoCircleFilled,
  WarningOutlined,
  ApiOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import { formatDateTimeDetail } from "../utils/formatLocaleDate";
import type { TFunction } from "i18next";
import { AdminShell } from "../layouts/AdminShell";
import { useAdminRefund } from "../api/hooks";
import { formatTransactionId } from "../utils/transactionUi";
import { useElementHeight } from "../hooks/useElementHeight";

const REFUND_DETAIL_PAGE_CLASS = "min-h-[calc(100vh-64px)] pb-24 max-[1280px]:!px-0";
const REFUND_DETAIL_TOPBAR_CLASS = "fixed right-0 top-16 z-40 flex items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-8 py-3.5 shadow-[0_8px_20px_rgba(25,28,29,0.03)] left-64 max-[900px]:left-[76px] max-[900px]:px-4 min-[901px]:max-[1280px]:left-[220px] min-[901px]:max-[1280px]:px-5";
const REFUND_DETAIL_GRID_CLASS = "mx-auto grid items-start gap-10 px-8 py-7 grid-cols-[minmax(0,820px)_minmax(280px,1fr)] max-[900px]:grid-cols-1 max-[900px]:px-4 max-[900px]:py-5 min-[901px]:max-[1280px]:grid-cols-[minmax(0,1fr)] min-[901px]:max-[1280px]:px-5";
const REFUND_DETAIL_CARD_CLASS = "flex w-full flex-col rounded-[32px] border border-transparent bg-white p-8 shadow-[0_12px_16px_rgba(25,28,29,0.04)]";
const REFUND_DETAIL_SIDE_PANEL_CLASS = "sticky top-24 flex flex-col gap-5 max-[1280px]:static";

type TimelineEvent = {
  label: string;
  time: string;
  staff: string;
  tone: "muted" | "brand" | "success" | "danger";
};

function SectionTitle({ icon, label, compact = false }: { icon: any; label: string; compact?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex size-8 items-center justify-center rounded-xl bg-[#fff7ed] text-[#ea580c]">
        {icon}
      </span>
      <span className={`${compact ? "text-[11px]" : "text-[13px]"} font-black uppercase tracking-[0.8px] text-[#534434]`}>
        {label}
      </span>
    </div>
  );
}

function InfoBlock({
  label,
  value,
  sub,
  icon,
  mono = false,
  accent = false,
}: {
  label: string;
  value: any;
  sub?: string;
  icon?: any;
  mono?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-[#eef0f2] bg-[#fbfaf8] p-4">
      <div className="text-[10px] font-black uppercase tracking-[0.8px] text-[#867461]">{label}</div>
      <div className="mt-2 flex min-w-0 items-center gap-2">
        {icon && (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-[#534434] shadow-[0_8px_20px_rgba(25,28,29,0.06)]">
            {icon}
          </span>
        )}
        <span className={`min-w-0 truncate text-sm font-extrabold ${mono ? "font-mono" : ""} ${accent ? "text-[#ea580c]" : "text-[#191c1d]"}`}>
          {value || "—"}
        </span>
      </div>
      {sub && <div className="mt-2 text-xs font-semibold text-[#867461]">{sub}</div>}
    </div>
  );
}

function DetailRow({ label, value, clickable = false }: { label: string; value: any; clickable?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#fbfaf8] px-4 py-3 text-sm">
      <span className="font-semibold text-[#534434]">{label}</span>
      <span className={`min-w-0 truncate text-right font-extrabold ${clickable ? "cursor-pointer text-[#ea580c]" : "text-[#191c1d]"}`}>
        {value || "—"}
      </span>
    </div>
  );
}

function toneClass(tone: TimelineEvent["tone"]) {
  if (tone === "danger") return "bg-[#ef4444] shadow-[0_0_0_4px_rgba(239,68,68,0.15)]";
  if (tone === "muted") return "bg-[#94a3b8] shadow-[0_0_0_4px_rgba(148,163,184,0.14)]";
  return "bg-[#10b981] shadow-[0_0_0_4px_rgba(16,185,129,0.15)]";
}

function buildTimeline(
  t: TFunction,
  lng: string,
  logs: Array<{ id: string; action: string; created_at: string; meta: Record<string, unknown> | null; staff: { name: string } | null }>,
  refundStatus: string,
  createdAt: string,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const dash = t("refundDetail.dash");

  const createLog = logs.find((l) => l.action === "refund.create" || l.action === "refund.created" || l.action === "create");
  events.push({
    label: t("refundDetail.timelineCreated"),
    time: formatDateTimeDetail(createLog?.created_at ?? createdAt, lng),
    staff: createLog?.staff?.name ?? t("refundDetail.staffSystem"),
    tone: "success",
  });

  const processLog = logs.find((l) =>
    l.action === "refund.reviewed" || l.action === "review" ||
    l.action === "refund.processing" || l.action === "update"
  );
  if (processLog) {
    events.push({
      label: t("refundDetail.timelineProcessing"),
      time: formatDateTimeDetail(processLog.created_at, lng),
      staff: processLog.staff?.name ?? t("refundDetail.staffAdmin"),
      tone: "brand",
    });
  }

  if (refundStatus === "success") {
    const completeLog = logs.find((l) => l.action === "refund.approve" || l.action === "refund.completed" || l.action === "approve");
    events.push({
      label: t("refundDetail.timelineCompleted"),
      time: completeLog ? formatDateTimeDetail(completeLog.created_at, lng) : dash,
      staff: completeLog?.staff?.name ?? t("refundDetail.staffAdmin"),
      tone: "success",
    });
  } else if (refundStatus === "rejected") {
    const rejectLog = logs.find((l) => l.action === "refund.reject" || l.action === "refund.rejected" || l.action === "reject");
    events.push({
      label: t("refundDetail.timelineRejected"),
      time: rejectLog ? formatDateTimeDetail(rejectLog.created_at, lng) : dash,
      staff: rejectLog?.staff?.name ?? t("refundDetail.staffAdmin"),
      tone: "danger",
    });
  } else if (refundStatus === "failed") {
    // Gateway declined the refund — distinct from an admin "rejected" decision.
    const failLog = logs.find((l) => l.action === "refund.approve" || l.action === "approve");
    events.push({
      label: t("refundDetail.timelineFailed"),
      time: failLog ? formatDateTimeDetail(failLog.created_at, lng) : dash,
      staff: t("refundDetail.staffGateway"),
      tone: "danger",
    });
  }

  return events.reverse();
}

export function RefundDetailScreen() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: refund, isLoading } = useAdminRefund(id);
  const [topbarRef, topbarHeight] = useElementHeight<HTMLDivElement>();

  if (isLoading) {
    return (
      <AdminShell>
        <div className="px-8 py-7">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      </AdminShell>
    );
  }

  if (!refund) {
    return (
      <AdminShell>
        <div className="px-8 py-7">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/refunds")}
            className="mb-4 !font-bold !text-[#534434]"
          >
            {t("refundDetail.back")}
          </Button>
          <div className="font-bold text-[#dc2626]">{t("refundDetail.notFound")}</div>
        </div>
      </AdminShell>
    );
  }

  // Same labels as the refund list/badges (refunds.pill*), so one refund never reads two ways.
  const PILL: Record<string, string> = { success: "pillSuccess", completed: "pillSuccess", approved: "pillSuccess", pending: "pillPending", rejected: "pillRejected", failed: "pillFailed" };
  const statusLabel = PILL[refund.status] ? t(`refunds.${PILL[refund.status]}`) : refund.status;
  const typeLabel = refund.type === "full"
    ? t("refundDetail.typeFull")
    : refund.type === "partial"
      ? t("refundDetail.typePartial")
      : refund.type;
  const customer = refund.order?.customer ?? refund.customer;
  const customerExtra = (customer ?? {}) as Record<string, unknown>;
  const customerPhone = typeof customerExtra.phone === "string" ? customerExtra.phone : null;
  const customerEmail = typeof customerExtra.email === "string" ? customerExtra.email : null;

  let transferMethod = "";
  let transferTime = "";
  let merchantNote = "";

  for (const log of refund.activity_logs ?? []) {
    if (log.meta?.transfer_method) {
      transferMethod = String(log.meta.transfer_method);
      transferTime = String(log.meta.transfer_time ?? "");
      merchantNote = String(log.meta.note ?? "");
      break;
    }
  }

  // Gateway auto-refund path (Fiuu) — approve was resolved via provider.refund()
  // rather than a manual transfer, per admin/routes.ts PATCH /admin/refunds/:id.
  const GATEWAY_REFUND_ID_PREFIX = "Gateway refund ID: ";
  const approveLog = (refund.activity_logs ?? []).find((l) => l.action === "refund.approve" || l.action === "approve");
  const isAutoGateway = approveLog?.meta?.path === "auto_gateway"
    || Boolean(refund.rejection_note?.startsWith(GATEWAY_REFUND_ID_PREFIX));
  const gatewayRefundId = refund.rejection_note?.startsWith(GATEWAY_REFUND_ID_PREFIX)
    ? refund.rejection_note.slice(GATEWAY_REFUND_ID_PREFIX.length)
    : null;

  if (!merchantNote && refund.rejection_note && !gatewayRefundId) merchantNote = refund.rejection_note;
  // For a gateway failure, rejection_note carries Fiuu's own error message —
  // show it as a gateway failure below, not as a generic internal note.
  const gatewayFailureReason = refund.status === "failed" ? refund.rejection_note : null;

  const rfdId = refundDisplayId(refund.id);
  const txnRef = formatTransactionId(refund.transaction?.id);
  const orderCode = refund.order?.order_code ?? t("refundDetail.dash");
  const timeline = buildTimeline(t, i18n.language, refund.activity_logs ?? [], refund.status, refund.created_at);

  const initiatorRole = refund.initiated_by_staff?.role;
  const requestSourceLabel = initiatorRole === "stall_staff"
    ? t("refundDetail.requestSourceStall", { defaultValue: "Stall App" })
    : initiatorRole === "admin" || initiatorRole === "superadmin"
      ? t("refundDetail.requestSourceAdmin", { defaultValue: "Admin Dashboard" })
      : t("refundDetail.requestSourcePos");

  return (
    <AdminShell>
      <div className={REFUND_DETAIL_PAGE_CLASS} style={{ paddingTop: topbarHeight ? topbarHeight + 24 : 57 }}>
        <div ref={topbarRef} className={REFUND_DETAIL_TOPBAR_CLASS}>
          <div className="flex min-w-0 items-center gap-3">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/refunds")}
              className="!h-10 !w-10 !rounded-xl !border-none !bg-transparent !p-0 !text-[#534434]"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <div className="truncate text-xs font-bold uppercase tracking-[0.8px] text-[#867461]">{t("refundDetail.title")}</div>
                  <div className="truncate text-base font-black text-[#191c1d]">{rfdId}</div>
                </div>
                <StatusBadge status={refund.status} kind="refund" label={statusLabel} size="md" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[11px] font-black uppercase tracking-[0.8px] text-[#867461]">{t("refundDetail.refundAmount")}</div>
              <div className="text-xl font-black text-[#ea580c]">RM {Number(refund.amount).toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className={REFUND_DETAIL_GRID_CLASS}>
          <div className="flex flex-col gap-6">
            <div className={`${REFUND_DETAIL_CARD_CLASS} gap-7`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.8px] text-[#867461]">{t("refundDetail.refundIdLabel")}</div>
                  <div className="mt-2 text-[34px] font-black leading-none text-[#191c1d]">{rfdId}</div>
                </div>
                <StatusBadge status={refund.type} kind="refundType" label={typeLabel} size="md" />
              </div>

              <div className="grid grid-cols-2 gap-4 max-[900px]:grid-cols-1">
                <InfoBlock label={t("refundDetail.amountReclaimedLabel")} value={`RM ${Number(refund.amount).toFixed(2)}`} icon={<DollarOutlined />} accent />
                <InfoBlock label={t("refundDetail.createdTime")} value={formatDateTimeDetail(refund.created_at, i18n.language)} icon={<ClockCircleFilled />} />
                <InfoBlock label={t("refundDetail.requestSource")} value={requestSourceLabel} icon={<InfoCircleFilled />} />
                <InfoBlock label={t("refundDetail.transactionId")} value={txnRef} icon={<TransactionOutlined />} mono />
              </div>
            </div>

            <div className={`${REFUND_DETAIL_CARD_CLASS} !px-7 !py-6`}>
              <div className="mb-5 flex items-center justify-between gap-3">
                <SectionTitle icon={<TransactionOutlined />} label={t("refundDetail.transactionInfo")} />
                <Button
                  type="text"
                  onClick={() => navigate(`/transactions/${refund.transaction?.id}?from=refunds`, { state: { activeNav: "/refunds" } })}
                  className="!h-10 !rounded-xl !border !border-[#ffedd5] !bg-[#fff7ed] !px-4 !text-xs !font-black !uppercase !tracking-[0.6px] !text-[#ea580c]"
                >
                  {t("refundDetail.viewTransaction")}
                </Button>
              </div>
              <div className="grid gap-3">
                <DetailRow label={t("refundDetail.transactionId")} value={<span className="font-mono">{txnRef}</span>} />
                <DetailRow
                  label={t("refundDetail.orderId")}
                  value={<span onClick={() => navigate(`/orders/${refund.order?.id ?? ""}`, { state: { activeNav: "/refunds" } })}>#{orderCode}</span>}
                  clickable
                />
                <DetailRow label={t("refundDetail.paymentMethod")} value={payMethodLabel(refund.transaction?.method, i18n.language) ?? refund.transaction?.method ?? t("refundDetail.dash")} />
                <DetailRow label={t("refundDetail.totalPaid")} value={`RM ${Number(refund.transaction?.amount ?? 0).toFixed(2)}`} />
              </div>
            </div>

            <div className={`${REFUND_DETAIL_CARD_CLASS} !px-7 !py-6`}>
              <div className="mb-5 flex items-center justify-between gap-3">
                <SectionTitle icon={<ClockCircleFilled />} label={t("refundDetail.processTimeline")} />
              </div>
              <div className="mt-6">
                <div className="flex flex-col gap-0">
                  {timeline.map((event, index) => {
                    const isLast = index === timeline.length - 1;
                    return (
                      <div key={`${event.label}-${index}`} className="group flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`z-10 mt-1 size-2.5 rounded-full ${toneClass(event.tone)}`} />
                          {!isLast && <div className="my-2 w-[2px] flex-1 rounded-full bg-[#f1f5f9] transition-colors group-hover:bg-[#e2e8f0]" />}
                        </div>
                        <div className={`flex-1 ${!isLast ? "pb-6" : ""}`}>
                          <div className="mb-1 text-[10px] font-black uppercase tracking-[0.8px] text-[#94a3b8]">
                            {event.time}
                          </div>
                          <div className="text-sm font-extrabold text-[#0f172a]">
                            {event.label}
                          </div>
                          {event.staff && (
                            <div className="mt-2 rounded-xl bg-[#f8fafc] p-3 text-xs font-semibold leading-relaxed text-[#64748b]">
                              {event.staff}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className={REFUND_DETAIL_SIDE_PANEL_CLASS}>
            <div className={`${REFUND_DETAIL_CARD_CLASS} !px-7 !py-6`}>
              <div className="mb-5">
                <SectionTitle icon={<UserOutlined />} label={t("refundDetail.customerInfo")} compact />
              </div>
              {customer ? (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3 rounded-2xl bg-[#fbfaf8] p-4">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[#fff7ed] text-lg font-black text-[#ea580c]">
                      {(customer.name ?? t("refundDetail.guest")).charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-black text-[#191c1d]">{customer.name ?? t("refundDetail.guest")}</div>
                      <div className="mt-1 text-xs font-semibold text-[#867461]">{t("orderDetail.customerShortId", { id: customer.id.slice(-4) })}</div>
                    </div>
                  </div>
                  {customerPhone && <DetailRow label={t("refundDetail.phone")} value={customerPhone} />}
                  {customerEmail && <DetailRow label={t("refundDetail.email")} value={customerEmail} />}
                </div>
              ) : (
                <div className="rounded-2xl bg-[#fbfaf8] px-4 py-5 text-sm font-bold text-[#867461]">{t("refundDetail.noCustomer")}</div>
              )}
            </div>

            <div className={`${REFUND_DETAIL_CARD_CLASS} !px-7 !py-6`}>
              <div className="mb-5">
                <SectionTitle icon={<FileTextOutlined />} label={t("refundDetail.refundReason")} compact />
              </div>
              <div className="rounded-2xl bg-[#fbfaf8] px-4 py-4 text-sm font-semibold leading-6 text-[#191c1d]">
                {cancelReasonLabel(refund.reason, i18n.language) || t("refundDetail.dash")}
              </div>
            </div>

            {gatewayFailureReason && (
              <div className={`${REFUND_DETAIL_CARD_CLASS} !px-7 !py-6 !border-[#fecaca] !bg-[#fffafa]`}>
                <div className="mb-5">
                  <SectionTitle icon={<WarningOutlined />} label={t("refundDetail.gatewayFailureTitle")} compact />
                </div>
                <div className="rounded-2xl bg-white px-4 py-4 text-sm font-semibold leading-6 text-[#7f1d1d]">
                  {gatewayFailureReason}
                </div>
              </div>
            )}

            {merchantNote && !transferMethod && !gatewayFailureReason && (
              <div className={`${REFUND_DETAIL_CARD_CLASS} !px-7 !py-6`}>
                <div className="mb-5">
                  <SectionTitle icon={<InfoCircleFilled />} label={t("refundDetail.internalNote")} compact />
                </div>
                <div className="rounded-2xl bg-[#fbfaf8] px-4 py-4 text-sm font-semibold leading-6 text-[#191c1d]">
                  {merchantNote}
                </div>
              </div>
            )}

            {isAutoGateway && (
              <div className="overflow-hidden rounded-3xl border border-[#e5e7eb] bg-white shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
                <div className="h-1 bg-[#047857]" />
                <div className="px-5 pb-5 pt-4">
                  <SectionTitle icon={<ApiOutlined />} label={t("refundDetail.autoRefundBadge")} compact />
                  {gatewayRefundId && (
                    <div className="mt-5 rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <div className="text-[10px] font-black uppercase tracking-[0.8px] text-[#94a3b8]">{t("refundDetail.gatewayRefundIdLabel")}</div>
                      <div className="mt-1 text-sm font-extrabold font-mono text-[#0f172a]">{gatewayRefundId}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {transferMethod && (
              <div className="overflow-hidden rounded-3xl border border-[#e5e7eb] bg-white shadow-[0_12px_16px_rgba(25,28,29,0.04)]">
                <div className="h-1 bg-[#006c49]" />
                <div className="px-5 pb-5 pt-4">
                  <SectionTitle icon={<DollarOutlined />} label={t("refundDetail.transferDetails")} compact />
                  <div className="mt-5 grid gap-3">
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <div className="text-[10px] font-black uppercase tracking-[0.8px] text-[#94a3b8]">{t("refundDetail.method")}</div>
                      <div className="mt-1 text-sm font-extrabold text-[#0f172a]">{payMethodLabel(transferMethod, i18n.language) ?? transferMethod}</div>
                    </div>
                    {transferTime && (
                      <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                        <div className="text-[10px] font-black uppercase tracking-[0.8px] text-[#94a3b8]">{t("refundDetail.time")}</div>
                        <div className="mt-1 text-sm font-extrabold text-[#0f172a]">{transferTime}</div>
                      </div>
                    )}
                  </div>
                  {merchantNote && (
                    <div className="mt-4 rounded-xl bg-[#f8fafc] p-3 text-xs font-semibold leading-relaxed text-[#64748b]">
                      <div className="mb-1 text-[10px] font-black uppercase tracking-[0.8px] text-[#94a3b8]">{t("refundDetail.merchantNoteUpper")}</div>
                      {merchantNote}
                    </div>
                  )}
                  </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
